import ko from "knockout";
import dateHelper from "flow/utils/dateHelper";
import viewHtml from "./toDate.html";
import { MaybeObservable, MaybeObservableAttributes, ObservableAttributes } from "flow/interfaces/MaybeObservable";
import createRequiredObservable from "flow/koHelpers/createRequiredObservable";
import { selectIdStatic } from "flow/koHelpers/selectId";
import { selectLabel, selectPlaceholder } from "flow/koHelpers/selectParam";
import createEnabler from "flow/koHelpers/createEnablerDisabler";
import { interceptAttributesWithDefaultsIgnoreId } from "flow/koHelpers/interceptAttributes";
import metaCache from "flow/utils/metaCache";

export interface IToDateParams {
    attr?: MaybeObservableAttributes;
    daysAfterStartDate?: number;
    enable?: MaybeObservable<boolean>;
    disable?: MaybeObservable<boolean>;
    enableMaxWidth?: boolean;
    startDate?: string;
    value: KnockoutObservable<string>;
    valueUpdate?: string;
    meta?: MaybeObservable<string>;
    text?: MaybeObservable<string>;
    metaPlaceholder?: MaybeObservable<string>;
    placeholder?: MaybeObservable<string>;
}

class ToDateViewModel {
    attributes: ObservableAttributes;
    daysAfterStartDate: number;
    enable: KnockoutObservable<boolean>;
    enableMaxWidth: boolean;
    startDate: MaybeObservable<string>;
    value: KnockoutObservable<string>;
    valueUpdate: string;
    label: KnockoutObservable<string>;
    required: KnockoutObservable<boolean>;
    id: string;
    ariaLabel: KnockoutComputed<string>;

    constructor(params: IToDateParams, input: Element) {
        this.value = params.value;
        this.valueUpdate = params.valueUpdate;
        this.required = createRequiredObservable(params.value);
        this.id = selectIdStatic("ko_todate", params.attr);
        this.label = selectLabel(params.meta, params.text);
        this.enable = createEnabler(params.enable, params.disable);

        this.attributes = interceptAttributesWithDefaultsIgnoreId(
            {
                autocomplete: "off",
                placeholder: selectPlaceholder(params.metaPlaceholder, params.placeholder)
            },
            params.attr
        );
        input.setAttribute("id", this.id);

        this.ariaLabel = ko.pureComputed(() => {
            return this.value()
                ? `${metaCache.translate("FlowHtml.Translations.ChangeDate")}, ${this.value()}`
                : `${metaCache.translate("FlowHtml.Translations.ChooseDate")}`;
        });

        this.startDate = params.startDate || ko.observable(dateHelper.toShortDate(new Date(0)));
        this.enableMaxWidth = params.enableMaxWidth ? params.enableMaxWidth : false;
        this.daysAfterStartDate = 1;

        if (typeof params.daysAfterStartDate === "number") {
            this.daysAfterStartDate = params.daysAfterStartDate;
        }
    }
}

ko.components.register("ko-todate", {
    viewModel: {
        createViewModel(params: any, componentInfo: KnockoutComponentTypes.ComponentInfo) {
            const input = (componentInfo.element as HTMLElement).querySelector("input");
            return new ToDateViewModel(params, input);
        }
    },
    template: viewHtml,
    synchronous: true
});
